import React from 'react';
import { Link } from 'react-router-dom';
import { Terminal, PhoneCall } from 'lucide-react';
import { Hero, Band, CenterHead, IconTile, SURFACE, HAIRLINE, SHADOW, T, DemoLink } from './siteUI';
import { Breadcrumb, CtaBand } from './DemoSiteLayout';

/* Developer-facing API page: REST for voice agents, calls, transcripts. Two quickstarts with code. */

const QUICKSTARTS = [
  {
    icon: Terminal,
    title: 'Authenticate',
    body: 'Every request takes a bearer token. Create one in the dashboard and pass it in the Authorization header.',
    code: `curl https://api.freshroad.com/v1/agents \\
  -H "Authorization: Bearer $FR_API_KEY"`,
  },
  {
    icon: PhoneCall,
    title: 'Start an agent call',
    body: 'Point an agent at a phone number and FreshRoad handles speech, language, and turn-taking.',
    code: `POST /v1/calls
{
  "agentId": "hotel_front_desk",
  "to": "+18885551234"
}`,
  },
];

const CodeBlock = ({ children }) => (
  <pre className="mt-4 overflow-x-auto rounded-xl bg-slate-900 px-4 py-3 text-[12px] leading-relaxed text-slate-100">
    <code>{children}</code>
  </pre>
);

const DemoSiteApiDev = () => (
  <>
    <Breadcrumb trail={['Home', 'Developers']} />

    <section className="py-8">
      <Hero
        eyebrow="Developers"
        title="Build on FreshRoad"
        deck={
          <>
            A REST API for voice agents,
            <br />
            calls, and transcripts.
          </>
        }
        actions={
          <>
            <DemoLink />
            <a
              href="https://docs.freshroad.com"
              target="_blank"
              rel="noreferrer"
              className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-white px-6 py-3 text-[14px] font-semibold text-teal-700 transition-colors hover:bg-teal-50 ${HAIRLINE} ${SHADOW.card}`}
            >
              Read the docs
            </a>
          </>
        }
      >
        Authenticate with a bearer token, then start agents, place calls, stream transcripts, and
        manage knowledge bases programmatically. JSON in, JSON out.
      </Hero>
    </section>

    <Band flush className="px-6 py-10 md:px-10">
      <CenterHead title="Two quickstarts" subtitle="Copy, paste, and you’re calling the API." />
      <div className="mx-auto mt-8 grid max-w-[880px] gap-5 md:grid-cols-2">
        {QUICKSTARTS.map(({ icon, title, body, code }) => (
          <div key={title} className={`${SURFACE.card} flex flex-col p-6`}>
            <IconTile icon={icon} />
            <p className={`mt-4 ${T.h3}`}>{title}</p>
            <p className={`mt-2 ${T.body}`}>{body}</p>
            <CodeBlock>{code}</CodeBlock>
          </div>
        ))}
      </div>
    </Band>

    <CtaBand
      title="Start building today."
      subtitle="Grab an API key and ship your first call — or book a demo and we’ll walk you through it."
      primary="Book a demo"
    />
  </>
);

export default DemoSiteApiDev;
